/**
 * Improvement projects (docs/plans/2026-10-05_improvement-projects.md): a Six Sigma project the server composes from a
 * brief's trend, a defect, a driver or a recommendation, which a person then adopts and runs through DMAIC. Every
 * composed number is a brief fact; a person edits only the charter's words, the target, the owner and team, and notes.
 *
 *   POST  /v1/twin/improvement-projects            {source, title?, method?}: 201 the composed draft, run as a DMAIC
 *                                                  (the default) or an A3 (migration 0203)
 *   POST  /v1/twin/improvement-projects/suggest    {source}: {method, reason, existing?}, the method that suits it
 *     409 project_exists                           a draft or active project already has the same Y and scope (its id
 *                                                  and status)
 *     404 improvement_projects_off                 a gate is shut: the platform's, or the org's switch
 *     422 no_baseline                              the briefs state no rate for the Y (the server's words say why)
 *     400 invalid_source                           among others, a typed scope the plant does not know (it lists them)
 *   GET   /v1/twin/improvement-projects?status=    the summaries, newest first
 *   GET   /v1/twin/improvement-projects/{id}       one project
 *   PATCH /v1/twin/improvement-projects/{id}       title, status, phase and the editable text fields
 *     400 a target above the baseline; 403 closing or dropping another person's project (its creator or an org admin)
 *   GET   /v1/twin/settings/improvement-projects   {enabled, platform_enabled}
 *   PUT   /v1/twin/settings/improvement-projects   {enabled} (org admin)
 *     403 improvement_projects_not_enabled         the platform has not opened projects to the org
 *
 * Two gates, both shut by default: the platform's (platform_enabled: every org by the API process, or one org by an
 * operator) and, behind it, the org admin's switch. `enabled` is both open. The web fails closed: no live API, a read
 * still on its way, a failed read, or a platform that does not serve the route (any other 404) all read as off, so the
 * page, the navigation item and the action show nothing; and the org admin's switch itself shows only while the
 * platform's gate is open.
 */
import { useEffect } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import { errorCodeOf } from "./error";
import { useBriefLive } from "./brief";

export type ProjectStatus = "draft" | "active" | "closed" | "dropped";
/** How a project is run: Six Sigma's DMAIC, or an A3 (one page: plan, do, check, act). */
export type ProjectMethod = "dmaic" | "a3";
/** A DMAIC's phases, then an A3's. */
export type ProjectPhase = "define" | "measure" | "analyze" | "improve" | "control" | "plan" | "do" | "check" | "act";

/** What a project was composed from. */
export type ProjectSourceKind = "brief_defect" | "brief_driver" | "period_trend" | "recommendation" | "manual";

export interface ProjectSourceScope {
  line?: string;
  shift?: string;
  part?: string;
  grade?: string;
}

export interface ProjectSource {
  kind: ProjectSourceKind;
  defect?: string;
  hypothesis_id?: string;
  mechanism_id?: string;
  stratum?: string;
  recommendation_key?: string;
  period?: { cadence: string; period_key: string };
  scope?: ProjectSourceScope;
}

export interface ProjectScope {
  /** The plant area the Y's defect family belongs to ("sand_system"), and its words. */
  area: string;
  area_label: string;
  equipment: { id: string; name: string }[];
  line?: string;
  shift?: string;
  part?: string;
  grade?: string;
  /** The scope in words. */
  label: string;
}

export interface ProjectQuantity {
  value: number;
  unit: string;
}

/** A period brief's own baseline rate, beside the period's rate: the periods before it. */
export interface ProjectPeriodBaseline {
  value: number;
  fact_key?: string;
  window: { from: string; to: string };
  /** How many periods it holds. */
  periods: number;
}

/**
 * The Y where it stands: a rate (the Y's scrapped pieces over the castings of one window), both counts kept, never a
 * piece count. `value` is the rate and its unit always `fraction`. `units_n` is what the rate rests on: the window's
 * lots for the overall brief's lots window, but 1 for one period's brief, so never word a period's baseline from it:
 * `basis` names the window in words and `period` names the period brief. The counts and `basis` are optional here
 * where a server may omit them.
 */
export interface ProjectBaseline extends ProjectQuantity {
  window: { from: string; to: string };
  units_n: number;
  fact_key?: string;
  /** The Y's scrapped pieces in the window, and the castings of it: the rate's denominator. */
  pieces?: number;
  castings?: number;
  pieces_fact_key?: string;
  castings_fact_key?: string;
  /** The window in words ("the month 2026-07, the latest closed month", "the brief's lots window, ..."). */
  basis?: string;
  /** The period brief the rate is from; absent for the overall brief's lots window or several months. */
  period?: { cadence: string; period_key: string };
  /**
   * The period briefs a baseline over several of them sums its counts over (the three latest closed months), newest first.
   * Then `units_n` is the number of periods, never lots. Absent for one brief or the lots window.
   */
  periods?: { cadence: string; period_key: string }[];
  period_baseline?: ProjectPeriodBaseline;
  /**
   * The baseline rests on fewer castings or pieces than a baseline needs (the lots window, taken whatever it holds):
   * its composed target holds it, and no change can be judged against it. Absent otherwise (and from a server before it).
   */
  thin?: boolean;
}

export interface ProjectTarget extends ProjectQuantity {
  /** Where the target comes from, in words ("half the baseline"). */
  basis: string;
}

export interface ProjectCtq {
  y: { kind: "defect" | "param"; key: string; label: string; unit: string };
  baseline: ProjectBaseline;
  target: ProjectTarget;
}

export interface ProjectCharter {
  problem: string;
  business_case: string;
  goal: string;
  in_scope: string[];
  out_of_scope: string[];
  timeline_weeks: number;
  /** The timeline is the composer's default (12 weeks), no data's, until a person sets it. */
  timeline_defaulted?: boolean;
  owner?: string;
  team: string[];
  /** A person wrote the goal: a target edit keeps it rather than writing the goal's sentence again. */
  goal_edited?: boolean;
  /** Which window the result is judged against, in plain words: the baseline's window, its length and its castings. Composed. */
  comparison: string;
  /** The foundry playbook's word on why the Y matters, attributed to it: textbook knowledge, never the data's finding. */
  playbook_says?: string;
}

export interface ProjectX {
  input: string;
  label: string;
  unit: string;
  mechanism_id: string;
  measured: boolean;
  /** The ends (`lo` and `hi`, as the Plant limits page names them): its process window, or the brief's suggested range. */
  limits?: { lo?: number | null; hi?: number | null; source: "process_window" | "suggested_range" | string };
  /** Not composed yet (the brief does not carry it): absent until it is. */
  gauge_verified?: boolean;
}

export interface ProjectHypothesis {
  mechanism_id: string;
  statement: string;
  verdict?: string;
  evidence?: { n: number; effect_text: string; fact_keys: string[] };
  status: string;
}

export interface ProjectMeasurementGap {
  input: string;
  label: string;
  why: string;
  /** The mechanisms recording it would let the plant test, by their titles. */
  unlocks: string[];
  pieces_at_stake: number;
  /** Where to record it: the area's equipment, in words. */
  where: string;
  /** How a foundry records it, the usual way first. */
  how_to_record: string[];
  frequency?: string;
  /** 1 first, by the pieces at stake. */
  priority: number | string;
}

export interface ProjectCandidate {
  recommendation_key: string;
  text: string;
  mechanism_id: string;
}

export interface ProjectControlRow {
  input: string;
  label: string;
  lo?: number | null;
  hi?: number | null;
  check: string;
  frequency: string;
  /** The frequency is the composer's default ("Every shift"): the input's grain names none. */
  frequency_defaulted?: boolean;
  owner?: string;
  reaction: string;
  /** The reaction (and the owner, where the row named none) is the plant's own plan, read from the input's window on the Limits page (B3 T4). */
  reaction_from_window?: boolean;
}

/** The method the composer suggests for a source, and why, in words whose every number is a brief fact. */
export interface ProjectMethodSuggestion {
  method: ProjectMethod;
  reason: string;
}

/** How the plant's data stand on a mechanism, as the brief reads it. */
export type ProjectStanding = "supported" | "suggestive" | "not_supported" | "untestable" | "not_tested";

/** The fishbone's categories an A3's cause is placed in. */
export type FishboneCategory = "people" | "machine" | "method" | "material" | "measurement" | "environment";

/** One mechanism behind the Y on an A3's root cause; its whys and its category are the team's. */
export interface ProjectA3Cause {
  key: string;
  mechanism_id: string;
  title: string;
  standing: ProjectStanding | string;
  likely?: boolean;
  evidence: string[];
  playbook_says?: string;
  check: string[];
  whys: string[];
  category: FishboneCategory | "";
}

/** One step of an A3's plan: what, who, by when (a plant day, YYYY-MM-DD, or ""), done, and its countermeasure's key. */
export interface ProjectA3Step {
  what: string;
  /** Who, in words: the owner's name when a member was picked, else whoever the team wrote. */
  who: string;
  /** The step's owner as a member of the org (users.id), absent or "" for none (B3 T1). */
  owner?: string;
  /** The due date, a plant day: a step not done past it is overdue. */
  when: string;
  done: boolean;
  recommendation_key?: string;
  /** The step planned as a trial of its countermeasure (B3 T5); absent for a plain step. */
  trial?: ProjectA3Trial;
}

/** A trial's schedule: the change on throughout, or a switchback alternating it on and off by plant day or by shift. */
export type ProjectTrialSchedule = "continuous" | "switchback_day" | "switchback_shift";

/**
 * A plan step planned as a trial (B3 T5; the Go mirror is improvement_projects_a3.go ProjectA3Trial): where (one line,
 * or a set of parts; neither for the whole plant), its schedule, its length in plant days and first day, the fewest
 * castings in scope a verdict needs (0 for the effect check's own rule), and the day it was ended on.
 */
export interface ProjectA3Trial {
  line?: string;
  parts?: string[];
  schedule: ProjectTrialSchedule;
  days: number;
  start?: string;
  min_castings: number;
  ended?: string;
}

/**
 * An A3's own page over the composed sections: its background, current condition, goal and countermeasures are the
 * charter's, the CTQ's and Improve's; this holds its root cause, its plan and its follow-up.
 */
export interface ProjectA3 {
  causes: ProjectA3Cause[];
  plan: ProjectA3Step[];
  follow_up: { recommendation_keys: string[]; notes: string };
}

/** What a project was composed from, kept on it so it outlives the analysis run's retention. Any field may be absent. */
export interface ProjectProvenance {
  run_id?: string;
  run_done_at?: string;
  stats_version?: string;
  brief_engine?: string;
  brief_generated_at?: string;
  method_suggestion?: ProjectMethodSuggestion;
  /** Marks a past project, recorded after it ran: absent for one opened on the platform. */
  backfill?: ProjectBackfill;
}

/**
 * A past project's days: the plant days it opened and closed (absent while it runs) and when it was recorded. Its
 * baseline is from before it opened; its analysis is the plant's data when it was recorded.
 */
export interface ProjectBackfill {
  opened_on: string;
  closed_on?: string;
  recorded_at: string;
  /** The plant day it was recorded, the day a page shows. */
  recorded_on?: string;
}

export interface ImprovementProject {
  id: string;
  title: string;
  status: ProjectStatus;
  phase: ProjectPhase;
  method: ProjectMethod;
  composer_version: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  source: ProjectSource;
  scope: ProjectScope;
  ctq: ProjectCtq;
  charter: ProjectCharter;
  measure: { xs: ProjectX[]; note: string };
  analyze: { hypotheses: ProjectHypothesis[] };
  measurement_gaps: ProjectMeasurementGap[];
  improve: { candidates: ProjectCandidate[] };
  control: { plan: ProjectControlRow[] };
  provenance?: ProjectProvenance;
  /** An A3's own page (method a3); absent for a DMAIC. */
  a3?: ProjectA3;
}

/** One project in the list. */
export interface ImprovementProjectSummary {
  id: string;
  title: string;
  status: ProjectStatus;
  phase: ProjectPhase;
  method: ProjectMethod;
  ctq: { y: { kind: "defect" | "param"; key: string; label: string } };
  scope_label: string;
  created_at: string;
  updated_at?: string;
}

/** The fields a person may change: the composed facts (baseline, evidence, gaps) are not among them. */
export interface ImprovementProjectPatch {
  title?: string;
  status?: ProjectStatus;
  phase?: ProjectPhase;
  charter?: Partial<
    Pick<ProjectCharter, "problem" | "business_case" | "goal" | "in_scope" | "out_of_scope" | "timeline_weeks" | "owner" | "team">
  >;
  ctq?: { target: ProjectTarget };
  measure?: { note: string };
  /** An A3's own page: each named cause's whys and category, the plan's steps (all of them), the follow-up's notes. */
  a3?: {
    causes?: { key: string; whys?: string[]; category?: FishboneCategory | "" }[];
    plan?: ProjectA3Step[];
    follow_up?: { notes: string };
  };
}

/** The two gates (GET and PUT /v1/twin/settings/improvement-projects), both shut by default. */
export interface ImprovementProjectsSettings {
  /** Projects are on for the org: the platform's gate is open and the org admin's switch is on. */
  enabled: boolean;
  /** The platform has opened projects to the org, so the org admin's switch takes effect (and is shown). */
  platform_enabled: boolean;
}

// ---- reading the wire ----

const list = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** A method as the page reads it: a3 only where the server says so; a server before A3 sends none, a DMAIC. */
export function methodOf(raw: unknown): ProjectMethod {
  return raw === "a3" ? "a3" : "dmaic";
}

function a3Of(raw: ProjectA3 | null | undefined): ProjectA3 {
  return {
    causes: list(raw?.causes).map((c) => ({
      ...c,
      title: str(c.title),
      evidence: list(c.evidence),
      check: list(c.check),
      whys: list(c.whys),
      category: (str(c.category) as FishboneCategory | ""),
    })),
    plan: list(raw?.plan).map((st) => ({ ...st, what: str(st.what), who: str(st.who), when: str(st.when), done: st.done === true })),
    follow_up: { recommendation_keys: list(raw?.follow_up?.recommendation_keys), notes: str(raw?.follow_up?.notes) },
  };
}

/** A project as the page reads it: a Go nil list as an empty one, so a section never meets null. */
export function improvementProjectOf(raw: ImprovementProject): ImprovementProject {
  const charter = raw.charter ?? ({} as ProjectCharter);
  const method = methodOf(raw.method);
  return {
    ...raw,
    method,
    a3: method === "a3" ? a3Of(raw.a3) : undefined,
    scope: { ...raw.scope, equipment: list(raw.scope?.equipment), label: str(raw.scope?.label) },
    charter: {
      ...charter,
      problem: str(charter.problem),
      business_case: str(charter.business_case),
      goal: str(charter.goal),
      comparison: str(charter.comparison),
      in_scope: list(charter.in_scope),
      out_of_scope: list(charter.out_of_scope),
      team: list(charter.team),
    },
    measure: { xs: list(raw.measure?.xs), note: str(raw.measure?.note) },
    analyze: {
      hypotheses: list(raw.analyze?.hypotheses).map((h) =>
        h.evidence ? { ...h, evidence: { ...h.evidence, fact_keys: list(h.evidence.fact_keys) } } : h,
      ),
    },
    measurement_gaps: list(raw.measurement_gaps).map((g) => ({
      ...g,
      unlocks: list(g.unlocks),
      how_to_record: list(g.how_to_record),
    })),
    improve: { candidates: list(raw.improve?.candidates) },
    control: { plan: list(raw.control?.plan) },
  };
}

/** One page of the list: the projects, how many the org has in all, and where this page sits (the server's `limit` is 50). */
export interface ProjectsPage {
  items: ImprovementProjectSummary[];
  total: number;
  limit: number;
  offset: number;
}

/** The list as the server sends it: `{items, total, limit, offset}`, or a bare array from a server that sends one. */
type ProjectListWire =
  | { items?: ImprovementProjectSummary[] | null; total?: number | null; limit?: number | null; offset?: number | null }
  | ImprovementProjectSummary[]
  | null;

const count0 = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : undefined);

/** A summary as the list reads it: its method a3 only where the server says so. */
const summaryOf = (s: ImprovementProjectSummary): ImprovementProjectSummary => ({ ...s, method: methodOf(s.method) });

/** A page as the page reads it. A total that falls short of what the page holds (or none) is read as what is held. */
function pageOf(raw: ProjectListWire): ProjectsPage {
  if (Array.isArray(raw)) return { items: raw.map(summaryOf), total: raw.length, limit: raw.length, offset: 0 };
  const items = list(raw?.items).map(summaryOf);
  const offset = count0(raw?.offset) ?? 0;
  return {
    items,
    total: Math.max(count0(raw?.total) ?? 0, offset + items.length),
    limit: count0(raw?.limit) ?? items.length,
    offset,
  };
}

/** Where the next page starts, or undefined where the last page reached the total (or came back empty). */
export function nextOffsetOf(last: ProjectsPage | null | undefined): number | undefined {
  if (!last || last.items.length === 0) return undefined;
  const next = last.offset + last.items.length;
  return next < last.total ? next : undefined;
}

/** The projects of every page read so far, and the org's total: null where a page said the feature is off, undefined before any. */
export function projectsOfPages(
  pages: readonly (ProjectsPage | null)[] | undefined,
): { items: ImprovementProjectSummary[]; total: number } | null | undefined {
  if (!pages || pages.length === 0) return undefined;
  if (pages.some((p) => p === null)) return null;
  const read = pages as ProjectsPage[];
  const items = read.flatMap((p) => p.items);
  return { items, total: Math.max(read[read.length - 1].total, items.length) };
}

// ---- the refusals ----

/** The org's switch is off: the server's own 404, never a route the platform does not serve. */
export const PROJECTS_OFF = "improvement_projects_off";
/** A draft or active project already has the same Y and scope. */
export const PROJECT_EXISTS = "project_exists";
/** An org admin's switch while the platform has not opened projects to the org (403). */
export const PROJECTS_NOT_ENABLED = "improvement_projects_not_enabled";

/** A 404 of any kind: the switch is off, or the platform does not serve improvement projects yet. Either shows nothing. */
export function isProjectsUnavailable(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 404 && errorCodeOf(err) !== "project_not_found";
}

/** The existing project's id a 409 project_exists names, under whichever key the server sends it. */
export function existingProjectIdOf(err: unknown): string | null {
  if (!(err instanceof ApiClientError) || err.status !== 409 || errorCodeOf(err) !== PROJECT_EXISTS) return null;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return null;
  for (const k of ["id", "project_id", "existing_id"]) {
    const v = body[k];
    if (typeof v === "string" && v !== "") return v;
  }
  const project = body.project as { id?: unknown } | undefined;
  return typeof project?.id === "string" && project.id !== "" ? project.id : null;
}

/** The open project's status a 409 project_exists names: a draft or an active one; none for anything else. */
export function existingProjectStatusOf(err: unknown): "draft" | "active" | undefined {
  if (!(err instanceof ApiClientError) || err.status !== 409 || errorCodeOf(err) !== PROJECT_EXISTS) return undefined;
  const status = (err.body as Record<string, unknown> | null)?.status;
  return status === "draft" || status === "active" ? status : undefined;
}

/** The org admin's switch was refused because the platform has not opened projects to the org (403). */
export function isNotOpenedToOrg(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 403 && errorCodeOf(err) === PROJECTS_NOT_ENABLED;
}

/** Both gates shut: what the cache holds once the platform says it has not opened projects to the org. */
const GATES_SHUT: ImprovementProjectsSettings = { enabled: false, platform_enabled: false };

/**
 * What a refused save tells the page about the gates: the platform's is shut (the switch was never the org's to turn),
 * so the cache holds both shut, the switch goes, and nothing asks again. Null for any other failure: the gates stand.
 */
export function settingsAfterSaveError(err: unknown): ImprovementProjectsSettings | null {
  return isNotOpenedToOrg(err) ? { ...GATES_SHUT } : null;
}

// ---- the calls ----

const BASE = "/v1/twin/improvement-projects";
const SETTINGS = "/v1/twin/settings/improvement-projects";

/** What creating a project came to: the new draft, the project that already covers it, or the switch off. */
export type CreateProjectResult =
  | { kind: "created"; project: ImprovementProject }
  | { kind: "exists"; id: string; status?: "draft" | "active" }
  | { kind: "off" };

export async function createImprovementProject(
  source: ProjectSource,
  title?: string,
  method?: ProjectMethod,
): Promise<CreateProjectResult> {
  try {
    const body: { source: ProjectSource; title?: string; method?: ProjectMethod } = { source };
    if (title && title.trim() !== "") body.title = title.trim();
    if (method) body.method = method;
    const project = improvementProjectOf(await api.post<ImprovementProject>(BASE, body));
    return { kind: "created", project };
  } catch (err) {
    const existing = existingProjectIdOf(err);
    if (existing) {
      const status = existingProjectStatusOf(err);
      return status ? { kind: "exists", id: existing, status } : { kind: "exists", id: existing };
    }
    if (isProjectsUnavailable(err)) return { kind: "off" };
    throw err;
  }
}

/** What the suggestion came to: the method that suits the source and why, with any open project it would meet; or off. */
export type SuggestionResult =
  | { kind: "suggested"; method: ProjectMethod; reason: string; existing?: { id: string; status: "draft" | "active"; method: ProjectMethod } }
  | { kind: "off" };

type SuggestionWire = {
  method?: unknown;
  reason?: unknown;
  existing?: { id?: unknown; status?: unknown; method?: unknown } | null;
} | null;

/** The method the server suggests for a source (POST .../suggest), read defensively; off where the switch is. */
export async function suggestImprovementProjectMethod(source: ProjectSource): Promise<SuggestionResult> {
  try {
    const raw = await api.post<SuggestionWire>(`${BASE}/suggest`, { source });
    const out: SuggestionResult = { kind: "suggested", method: methodOf(raw?.method), reason: str(raw?.reason) };
    const e = raw?.existing;
    if (e && typeof e.id === "string" && e.id !== "") {
      out.existing = { id: e.id, status: e.status === "active" ? "active" : "draft", method: methodOf(e.method) };
    }
    return out;
  } catch (err) {
    if (isProjectsUnavailable(err)) return { kind: "off" };
    throw err;
  }
}

/**
 * One page of the org's projects, newest first, from `offset` (the server's page is 50); null where the switch is off or
 * the platform does not serve them.
 */
export async function fetchImprovementProjects(status?: ProjectStatus, offset = 0): Promise<ProjectsPage | null> {
  try {
    const params = [status ? `status=${encodeURIComponent(status)}` : "", offset > 0 ? `offset=${offset}` : ""].filter(Boolean);
    return pageOf(await api.get<ProjectListWire>(`${BASE}${params.length > 0 ? `?${params.join("&")}` : ""}`));
  } catch (err) {
    if (isProjectsUnavailable(err)) return null;
    throw err;
  }
}

/** One project; null where the switch is off or the platform does not serve projects. A missing project throws. */
export async function fetchImprovementProject(id: string): Promise<ImprovementProject | null> {
  try {
    return improvementProjectOf(await api.get<ImprovementProject>(`${BASE}/${encodeURIComponent(id)}`));
  } catch (err) {
    if (isProjectsUnavailable(err)) return null;
    throw err;
  }
}

export async function patchImprovementProject(id: string, patch: ImprovementProjectPatch): Promise<ImprovementProject> {
  return improvementProjectOf(await api.patch<ImprovementProject>(`${BASE}/${encodeURIComponent(id)}`, patch));
}

type SettingsWire = { enabled?: boolean | null; platform_enabled?: boolean | null } | null | undefined;

/**
 * The gates as the page reads them, failing closed: the platform's is open only on an explicit true, and `enabled`
 * (both open) only with it, whatever else the body says.
 */
function settingsOf(raw: SettingsWire): ImprovementProjectsSettings {
  const platform_enabled = raw?.platform_enabled === true;
  return { enabled: platform_enabled && raw?.enabled === true, platform_enabled };
}

/** The org's gates; null where the platform does not serve them (so nothing shows, not even the admin's control). */
export async function fetchImprovementProjectsSettings(): Promise<ImprovementProjectsSettings | null> {
  try {
    return settingsOf(await api.get<SettingsWire>(SETTINGS));
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

/** The org admin's switch; refused (403 improvement_projects_not_enabled) while the platform's gate is shut. */
export async function saveImprovementProjectsSettings(enabled: boolean): Promise<ImprovementProjectsSettings> {
  return settingsOf(await api.put<SettingsWire>(SETTINGS, { enabled }));
}

/**
 * The signed-in person's platform user id, which a project's `created_by` names (the web's own auth id is the identity
 * provider's subject, a different id). Null where the server names none. A read that fails throws, so the page can tell
 * a person who is not the creator from one whose id it could not read, and offer to read it again (#1157 review).
 */
export async function fetchCallerId(): Promise<string | null> {
  const me = await api.get<{ id?: unknown } | null>("/v1/platform/auth/me");
  return typeof me?.id === "string" && me.id !== "" ? me.id : null;
}

export const IMPROVEMENT_CALLER_KEY = ["improvement-projects-caller"] as const;

/**
 * The caller's id, read once a session and only when `enabled` (an org admin needs none: they may end any project). A
 * failed read is tried twice more, and stays an error the page offers to read again, never a silent "not the creator".
 */
export function useProjectCallerId(enabled: boolean) {
  const live = useBriefLive();
  return useQuery({
    queryKey: IMPROVEMENT_CALLER_KEY,
    queryFn: fetchCallerId,
    enabled: live && enabled,
    staleTime: Infinity,
    retry: 2,
  });
}

// ---- the switch ----

/**
 * The rank that creates and edits projects: the one that answers the plant's questions and authors its knowledge
 * (member and up). Any member reads them; the server enforces both.
 */
export const PROJECT_WRITE_PERM = "knowledge.respond";

/** The gate, pure: on only for an explicit `enabled: true` with the platform's gate open, read from the live platform. Fails closed. */
export function improvementProjectsOn(live: boolean, settings: ImprovementProjectsSettings | null | undefined): boolean {
  return live && settings?.enabled === true && settings.platform_enabled === true;
}

export const IMPROVEMENT_SETTINGS_KEY = ["improvement-projects-settings"] as const;
export const IMPROVEMENT_PROJECTS_KEY = ["improvement-projects"] as const;
export const IMPROVEMENT_PROJECT_KEY = ["improvement-project"] as const;

/** The org's switch, read on the live platform only. `enabled` lets the sidebar ask only where the twin pack is on. */
/**
 * Forgets every project read, the list and each project: once the feature is off nothing of it is kept (the settings'
 * own read stays, since it is what says so). Anything on screen reads from the gate first, so a project is never shown
 * from here while the gate is shut; this is what keeps its title out of the cache and the copilot's context as well.
 */
export function dropProjectReads(qc: QueryClient): void {
  qc.removeQueries({ queryKey: IMPROVEMENT_PROJECTS_KEY });
  qc.removeQueries({ queryKey: IMPROVEMENT_PROJECT_KEY });
}

/** Drops the project reads while the gates are known to be shut (a read still on its way is not known). */
export function useDropProjectsWhenOff(shut: boolean): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (shut) dropProjectReads(qc);
  }, [shut, qc]);
}

export function useImprovementProjectsSettingsQuery(enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: IMPROVEMENT_SETTINGS_KEY,
    queryFn: fetchImprovementProjectsSettings,
    enabled: live && enabled,
    staleTime: 60_000,
  });
}

/** True only once the live platform says the org's switch is on. */
export function useImprovementProjectsEnabled(enabled = true): boolean {
  const live = useBriefLive();
  const q = useImprovementProjectsSettingsQuery(enabled);
  return enabled && improvementProjectsOn(live, q.data);
}

/**
 * Saves the switch (org admins); the page, the navigation and the actions read it again. Refused because the platform has
 * not opened projects to the org, the cache takes the gate as shut at once (the switch goes), with no read to follow.
 */
export function useSaveImprovementProjectsSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveImprovementProjectsSettings,
    onSuccess: (stored) => {
      qc.setQueryData(IMPROVEMENT_SETTINGS_KEY, stored);
      // Gone off: no project read before is kept to show. Still on: the list reads again.
      if (improvementProjectsOn(true, stored)) void qc.invalidateQueries({ queryKey: IMPROVEMENT_PROJECTS_KEY });
      else dropProjectReads(qc);
    },
    onError: (err) => {
      const shut = settingsAfterSaveError(err);
      if (shut) {
        qc.setQueryData(IMPROVEMENT_SETTINGS_KEY, shut);
        dropProjectReads(qc);
      }
    },
  });
}

/** The org's projects a page at a time (the server's page is 50): `fetchNextPage` reads the next while `hasNextPage`. */
export function useImprovementProjectsQuery(enabled = true) {
  const live = useBriefLive();
  return useInfiniteQuery({
    queryKey: IMPROVEMENT_PROJECTS_KEY,
    queryFn: ({ pageParam }) => fetchImprovementProjects(undefined, pageParam),
    initialPageParam: 0,
    getNextPageParam: nextOffsetOf,
    enabled: live && enabled,
    staleTime: 30_000,
  });
}

export function useImprovementProjectQuery(id: string | undefined, enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: [...IMPROVEMENT_PROJECT_KEY, id ?? ""],
    queryFn: () => fetchImprovementProject(id!),
    enabled: live && enabled && Boolean(id),
    staleTime: 30_000,
  });
}

/** Saves an edit and puts the stored project in the cache; the list reads again. */
export function usePatchImprovementProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ImprovementProjectPatch) => patchImprovementProject(id, patch),
    onSuccess: (stored) => {
      qc.setQueryData([...IMPROVEMENT_PROJECT_KEY, id], stored);
      void qc.invalidateQueries({ queryKey: IMPROVEMENT_PROJECTS_KEY });
    },
  });
}

/** Creates a project from a source; a new one is put in the cache and the list reads again. */
export function useCreateImprovementProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ source, title, method }: { source: ProjectSource; title?: string; method?: ProjectMethod }) =>
      createImprovementProject(source, title, method),
    onSuccess: (result) => {
      if (result.kind === "created") qc.setQueryData([...IMPROVEMENT_PROJECT_KEY, result.project.id], result.project);
      if (result.kind === "off") void qc.invalidateQueries({ queryKey: IMPROVEMENT_SETTINGS_KEY });
      void qc.invalidateQueries({ queryKey: IMPROVEMENT_PROJECTS_KEY });
    },
  });
}
